import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCleanUpCostDataRuns } from '@/data/cleanUpCostDataRun';
import { useCompanies } from '@/data/company';
import { Error } from '@/components/ui/error';
import { Loading } from '@/components/ui/loading';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

const formatDate = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleString();
};

const Page = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const companyUid = searchParams.get('companyUid') || '';
  const [selectedRun, setSelectedRun] = useState(null);

  const updateCompany = useCallback(
    (value) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (!value || value === 'all') {
          next.delete('companyUid');
        } else {
          next.set('companyUid', value);
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const companies = useCompanies('', false);
  const runs = useCleanUpCostDataRuns({ companyUid });

  if ((runs.isLoading && !runs.data) || (companies.isLoading && !companies.data)) {
    return <Loading />;
  }

  if (runs.error) {
    return <Error message="An error occurred loading cleanup cost data runs" />;
  }

  const companyList = companies.data?.query?.map((uid) => companies.data.content[uid]) || [];
  const runList = runs.data?.query?.map((uid) => runs.data.content[uid]) || [];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-lg font-semibold">Cleanup Runs History</h1>
        <p className="text-sm text-muted-foreground">History of AI-powered cost data cleanup runs across all companies</p>
      </div>

      <div className="flex items-center gap-4">
        <div className="w-[300px]">
          <Select value={companyUid || 'all'} onValueChange={updateCompany}>
            <SelectTrigger>
              <SelectValue placeholder="All Companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companyList.map((c) => (
                <SelectItem key={c.uid} value={c.uid}>
                  {c.company_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="text-sm text-muted-foreground">{runList.length} runs</span>
      </div>

      <div className="overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Project</TableHead>
              <TableHead>Record</TableHead>
              <TableHead>Prompt</TableHead>
              <TableHead>Run By</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runList.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No runs found
                </TableCell>
              </TableRow>
            )}
            {runList.map((run) => (
              <TableRow key={run.uid} className="cursor-pointer" onClick={() => setSelectedRun(run)}>
                <TableCell className="whitespace-nowrap">{run.company?.company_name || '-'}</TableCell>
                <TableCell className="whitespace-nowrap">{run.project_record?.project?.project_name || '-'}</TableCell>
                <TableCell className="whitespace-nowrap">{run.project_record ? `${run.project_record.record_scope} v${run.project_record.record_version}` : '-'}</TableCell>
                <TableCell className="max-w-[300px] truncate">{run.prompt || <span className="text-muted-foreground">None</span>}</TableCell>
                <TableCell className="whitespace-nowrap">{run.created_by_user ? `${run.created_by_user.firstname} ${run.created_by_user.lastname}` : '-'}</TableCell>
                <TableCell className="whitespace-nowrap">{formatDate(run.create_ts)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selectedRun} onOpenChange={(open) => !open && setSelectedRun(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Run Details</DialogTitle>
            <DialogDescription>
              {selectedRun?.company?.company_name} &mdash; {selectedRun?.project_record?.project?.project_name || 'Unknown Project'} &mdash; {formatDate(selectedRun?.create_ts)}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Prompt</label>
            <Textarea className="min-h-[300px] font-mono text-sm" readOnly value={selectedRun?.prompt || 'No prompt provided'} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Page;
